import { apiClient } from './api';

// Types
export interface Team {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description?: string;
  settings?: Record<string, unknown>;
  status: 'active' | 'archived';
  created_at: string;
  updated_at: string;
  member_count?: number;
}

/**
 * A team membership as the API actually returns it (REV-515).
 *
 * `user_teams` has a COMPOSITE primary key (user_id + team_id) and no `id`
 * column, so there is no membership id to return — identify a member by
 * `user_id`, which is also what the member endpoints are keyed by
 * (/teams/:teamId/members/:userId).
 *
 * `user` is nullable: User is a paranoid model, so a soft-deleted account
 * leaves the membership row behind with no user to join.
 */
export interface TeamMember {
  team_id: string;
  user_id: string;
  role: 'lead' | 'member';
  joined_at: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    avatar_url?: string | null;
  } | null;
}

export interface CreateTeamData {
  name: string;
  slug: string;
  description?: string;
  settings?: Record<string, unknown>;
}

export interface UpdateTeamData {
  name?: string;
  description?: string;
  status?: 'active' | 'archived';
  settings?: Record<string, unknown>;
}

export interface AddTeamMemberData {
  user_id: string;
  role?: 'lead' | 'member';
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  requestId?: string;
}

interface PaginatedResponse<T> {
  success: boolean;
  data: {
    items: T[];
    total: number;
    limit: number;
    offset: number;
  };
  requestId?: string;
}

// Team API
export const teamService = {
  // List teams in organization
  list: async (
    orgId: string,
    params?: { limit?: number; offset?: number; status?: 'active' | 'archived' }
  ) => {
    const response = await apiClient.get<PaginatedResponse<Team>>(
      `/v1/organizations/${orgId}/teams`,
      params
    );
    return response.data;
  },

  // Get team by ID or slug. The API returns the team directly in `data`
  // (teamController: `data: team`), not wrapped as `data.team` (REV-487).
  getById: async (orgId: string, teamId: string) => {
    const response = await apiClient.get<ApiResponse<Team>>(
      `/v1/organizations/${orgId}/teams/${teamId}`
    );
    return response.data;
  },

  // Create team
  create: async (orgId: string, data: CreateTeamData) => {
    const response = await apiClient.post<ApiResponse<Team>>(
      `/v1/organizations/${orgId}/teams`,
      data
    );
    return response.data;
  },

  // Update team
  update: async (orgId: string, teamId: string, data: UpdateTeamData) => {
    const response = await apiClient.put<ApiResponse<Team>>(
      `/v1/organizations/${orgId}/teams/${teamId}`,
      data
    );
    return response.data;
  },

  // Delete team
  delete: async (orgId: string, teamId: string) => {
    await apiClient.delete(`/v1/organizations/${orgId}/teams/${teamId}`);
  },

  // Check slug availability
  checkSlug: async (orgId: string, slug: string) => {
    const response = await apiClient.get<ApiResponse<{ available: boolean }>>(
      `/v1/organizations/${orgId}/teams/check-slug/${slug}`
    );
    return response.data;
  },

  // List team members
  getMembers: async (
    orgId: string,
    teamId: string,
    params?: { limit?: number; offset?: number }
  ) => {
    const response = await apiClient.get<PaginatedResponse<TeamMember>>(
      `/v1/organizations/${orgId}/teams/${teamId}/members`,
      params
    );
    return response.data;
  },

  // Add a member to / update a member's role in a team. Both return the
  // membership directly in `data` (teamController.js:249 addMember,
  // :319 updateMemberRole) — NOT wrapped as `data.member`, which is what these
  // read before REV-515 (same class of bug as REV-487).
  addMember: async (orgId: string, teamId: string, data: AddTeamMemberData) => {
    const response = await apiClient.post<ApiResponse<TeamMember>>(
      `/v1/organizations/${orgId}/teams/${teamId}/members`,
      data
    );
    return response.data;
  },

  // See the addMember comment above: same flat `data` envelope (REV-515).
  updateMemberRole: async (
    orgId: string,
    teamId: string,
    userId: string,
    role: 'lead' | 'member'
  ) => {
    const response = await apiClient.put<ApiResponse<TeamMember>>(
      `/v1/organizations/${orgId}/teams/${teamId}/members/${userId}`,
      { role }
    );
    return response.data;
  },

  // Remove member from team
  removeMember: async (orgId: string, teamId: string, userId: string) => {
    await apiClient.delete(`/v1/organizations/${orgId}/teams/${teamId}/members/${userId}`);
  },
};

export default teamService;
